<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? 'Editar lavado' : 'Registrar lavado' }}</h1>
    </div>

    <form class="custom-form" @submit.prevent="save" novalidate>
      <b-card tag="article" no-body style="max-width: 860px" class="custom-card">
        <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
          <h3>Datos del lavado</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-0">
          <section class="form-section">
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Vehículo *</label>
                  <select v-model="form.vehicle_id" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.vehicle_id.$error }">
                    <option :value="null" disabled>Seleccionar vehículo</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ v.license_plate }}</option>
                  </select>
                  <FormError v-if="$v.form.vehicle_id.$error && !$v.form.vehicle_id.required"
                    message="El vehículo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Fecha *</label>
                  <input type="date" v-model="form.wash_date" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.wash_date.$error }" />
                  <FormError v-if="$v.form.wash_date.$error && !$v.form.wash_date.required"
                    message="La fecha es requerida" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Tipo *</label>
                  <select v-model="form.wash_type" class="custom-form-control"
                    :class="{ 'custom-form-control-error': $v.form.wash_type.$error }">
                    <option :value="null" disabled>Seleccionar tipo</option>
                    <option v-for="t in washTypes" :key="t.key" :value="t.key">{{ t.label }}</option>
                  </select>
                  <FormError v-if="$v.form.wash_type.$error && !$v.form.wash_type.required"
                    message="El tipo es requerido" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Car wash (opcional)</label>
                  <input type="text" v-model="form.car_wash_name" class="custom-form-control"
                    placeholder="Nombre del establecimiento" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <label>Costo (S/, opcional)</label>
                  <input type="number" step="0.01" min="0" v-model.number="form.cost"
                    class="custom-form-control" placeholder="0.00" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Conductor (opcional)</label>
                  <select v-model="form.driver_id" class="custom-form-control">
                    <option :value="null">Sin conductor</option>
                    <option v-for="d in drivers" :key="d.id" :value="d.id">{{ d.full_name }}</option>
                  </select>
                </div>
              </div>

              <div class="col-md-12">
                <div class="form-group mb-0">
                  <label>Notas</label>
                  <textarea v-model="form.notes" class="custom-form-control" rows="2"
                    placeholder="Observaciones"></textarea>
                </div>
              </div>
            </div>
          </section>

          <section class="form-section">
            <h2 class="form-subtitle">Foto</h2>
            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Foto del lavado</label>
                  <input type="file" accept="image/*" class="custom-form-control" @change="onFile($event)" />
                  <small v-if="existing.has_photo" class="d-block mt-1">
                    <button type="button" class="photo-link" @click="viewPhoto">Ver foto actual</button>
                  </small>
                </div>
              </div>
            </div>
            <p class="text-muted small mb-0">Imagen hasta 5 MB.</p>
          </section>

          <section class="form-section">
            <div class="form-group-buttons text-right">
              <router-link :to="{ name: 'fleet-wash-list' }" class="button button-light">
                {{ editing ? 'Volver' : 'Cancelar' }}
              </router-link>
              <button class="button button-primary" type="submit">Guardar</button>
            </div>
          </section>
        </b-card-body>
      </b-card>
    </form>

    <b-modal v-model="photoModal.show" size="lg" hide-footer title="Foto del lavado">
      <div class="photo-frame text-center">
        <img v-if="photoModal.url" :src="photoModal.url" alt="Foto" class="photo-img" />
        <p v-else-if="photoModal.error" class="text-muted m-0 py-4">{{ photoModal.error }}</p>
        <p v-else class="text-muted m-0 py-4">Cargando imagen…</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormError } from 'wize-admin'
import { required } from 'vuelidate/lib/validators'
import { FleetWashService, VehicleService, DriverService } from '@/core/services'

const WASH_TYPES = [
  { key: 'BASIC', label: 'Básico' },
  { key: 'FULL', label: 'Completo' },
  { key: 'INTERIOR_ONLY', label: 'Solo interior' }
]
const MAX_PHOTO_BYTES = 5 * 1024 * 1024

export default {
  name: 'WashEditor',
  components: { FormError },
  data() {
    return {
      editing: false,
      vehicles: [],
      drivers: [],
      washTypes: WASH_TYPES,
      form: {
        vehicle_id: null,
        wash_date: null,
        wash_type: null,
        car_wash_name: null,
        cost: null,
        driver_id: null,
        notes: null
      },
      photoFile: null,
      existing: { has_photo: false },
      photoModal: { show: false, url: null, error: null }
    }
  },
  validations: {
    form: {
      vehicle_id: { required },
      wash_date: { required },
      wash_type: { required }
    }
  },
  methods: {
    onFile(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) { this.photoFile = null; return }
      if (!file.type.startsWith('image/')) {
        Alert.error('El archivo debe ser una imagen')
        event.target.value = ''
        return
      }
      if (file.size > MAX_PHOTO_BYTES) {
        Alert.error('La imagen supera el máximo de 5 MB')
        event.target.value = ''
        return
      }
      this.photoFile = file
    },
    buildPayload() {
      const f = this.form
      return {
        vehicle_id: f.vehicle_id,
        wash_date: f.wash_date,
        wash_type: f.wash_type,
        car_wash_name: f.car_wash_name || null,
        cost: f.cost != null && f.cost !== '' ? Number(f.cost) : null,
        driver_id: f.driver_id || null,
        notes: f.notes || null
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({ scrollTo: document.querySelector('.form-error'), updateHistory: false, offset: -50 })
        })
        return
      }
      try {
        await this.$store.dispatch('app/loading', true)
        const payload = this.buildPayload()

        if (!this.editing) {
          const res = await FleetWashService.create(payload)
          const id = res.payload.id
          if (this.photoFile) await FleetWashService.uploadPhoto(id, this.photoFile)
          Alert.success('Lavado registrado correctamente')
          // Warning no bloqueante por límite mensual (Alert.info: warning no garantizado en wize-admin).
          if (res.payload.warning) Alert.info(res.payload.warning)
          this.$router.push({ name: 'fleet-wash-list' })
        } else {
          const id = this.$route.params.id
          await FleetWashService.update(id, payload)
          if (this.photoFile) await FleetWashService.uploadPhoto(id, this.photoFile)
          this.photoFile = null
          Alert.success('Lavado actualizado correctamente')
          await this.load(id)
        }
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar el lavado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async load(id) {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetWashService.get(id)
        const p = res.payload
        this.form = {
          vehicle_id: p.vehicle_id,
          wash_date: p.wash_date ? p.wash_date.slice(0, 10) : null,
          wash_type: p.wash_type,
          car_wash_name: p.car_wash_name,
          cost: p.cost,
          driver_id: p.driver_id || null,
          notes: p.notes
        }
        this.existing = { has_photo: p.has_photo }
        this.editing = true
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudo cargar el lavado')
        this.$router.push({ name: 'fleet-wash-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async viewPhoto() {
      this.photoModal = { show: true, url: null, error: null }
      try {
        const res = await FleetWashService.getPhoto(this.$route.params.id)
        this.photoModal.url = res.payload ? res.payload.url : null
        if (!this.photoModal.url) this.photoModal.error = 'No hay foto disponible.'
      } catch (e) {
        console.error(e)
        this.photoModal.error = e.errors ? e.errors.message : 'No se pudo cargar la foto.'
      }
    },
    async loadVehicles() {
      try {
        const res = await VehicleService.list({ is_travelcab: true }, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.vehicles = payload.items || payload || []
      } catch (e) { console.error(e) }
    },
    async loadDrivers() {
      try {
        const res = await DriverService.list({}, { page: 0, size: 1000 })
        const payload = res.payload || {}
        this.drivers = payload.items || payload || []
      } catch (e) { console.error(e) }
    }
  },
  async created() {
    await this.loadVehicles()
    await this.loadDrivers()
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    } else if (this.$route.query.vehicle_id) {
      // Precarga el vehículo cuando se registra desde el dashboard.
      this.form.vehicle_id = Number(this.$route.query.vehicle_id)
    }
  }
}
</script>

<style lang="stylus" scoped>
.photo-link
  background none
  border none
  padding 0
  color #007bff
  cursor pointer
  text-decoration underline
.photo-frame
  background #f4f5f7
  border 1px solid #e5e7eb
  border-radius 8px
  min-height 220px
  display flex
  align-items center
  justify-content center
  overflow hidden
.photo-img
  max-width 100%
  max-height 70vh
  object-fit contain
</style>
